import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';
import { MatSort } from '@angular/material/sort';

import { AuditLogsService, BenefitManagementService, AuthService, DemoClientService } from '@core';
import {
  AuditLogsInterface,
  AuditLogsRequestInterface,
  Clients,
  EligibilityMenu,
} from '@interfaces';
import { AppCenterAnalytics, AppInsightsService, SharedService } from '@services';
import { LoaderService } from '@management';
import { formatDate, formatDateToMMDDYYYY } from 'src/app/util';
import { AppConstants } from 'src/app/app.constants';
import { dateValidator } from '@shared';

@Component({
    selector: 'app-audit-logs',
    templateUrl: './audit-logs.component.html',
    styleUrls: ['./audit-logs.component.scss'],
    standalone: false
})
export class AuditLogsComponent implements OnInit {
  public memberMenus: EligibilityMenu[] = [];
  public checkTouchForForm = false;
  public dataSource =
    new MatTableDataSource<AuditLogsInterface[]>([]);
  public searchAuditLogsFormGroup: FormGroup;
  public auditLogs$!: Observable<AuditLogsInterface[]>;
  public listOfClients$: Observable<Clients[]>;
  public hasAccessToAddMember: boolean;
  public maxDate = new Date();
  public isInternalUser: boolean;
  selection = new SelectionModel<any>(true, []);
  public filteredClients!: Observable<Clients[]>;
  public clientFilterControl = new FormControl();
  public categories$: Observable<string[]>;

  allFieldsEmpty = true;
  selectedClientId = '';
  dataLoaded = false;

  @ViewChild('paginator') paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  constructor(
    private readonly formBuilder: FormBuilder,
    public auditLogsService: AuditLogsService,
    private benefitService: BenefitManagementService,
    private readonly appInsightService: AppInsightsService,
    private loader: LoaderService,
    private authService: AuthService,
    private demoClientService: DemoClientService,
    private sharedService: SharedService
  ) { }

  public displayedColumns: string[] = [
    'select',
    'lastName',
    'firstName',
    'cardId',
    'birthDt',
    'personCode',
    'groupNumAndName',
    'dateAdded',
    'change',
    'changedBy',
    'comments',
    'errorCode',
    'errorMessage',
    'isSuccess'
  ];

  ngOnInit(): void {
    this.categories$ = this.auditLogsService.getCategories();
    this.appInsightService.trackPageView(AppCenterAnalytics.AUDIT_LOGS);
    const emailValidator = Validators.pattern(
      AppConstants.EMAIL_VALIDATION_PATTERN
    );
    this.searchAuditLogsFormGroup = this.formBuilder.group(
      {
        client: ['', [Validators.required]],
        cardId: ['', [Validators.pattern(/^[a-zA-Z0-9]{1,20}$/)]],
        firstName: ['', [Validators.maxLength(35)]],
        lastName: ['', [Validators.maxLength(35)]],
        from: [
          new Date(new Date().getFullYear(), new Date().getMonth(), 1), // set default start date to beginning of the current month
          [Validators.required, dateValidator()]],
        to: [new Date(), // set default end date to current date
        [Validators.required, dateValidator()]],
        email: ['', [emailValidator]],
        category: [''],
        status: ['']
      },
      { validators: this.dateRangeValidator }
    );

    this.searchAuditLogsFormGroup
      .get('from')
      .valueChanges.subscribe((startDate) => {
        // Update the end date filter when the start date changes
        this.searchAuditLogsFormGroup.get('to').updateValueAndValidity();
      });

    this.searchAuditLogsFormGroup.patchValue(
      this.auditLogsService.searchCriteriaCache
    );

    this.listOfClients$ = this.benefitService.getClientsListByAccess();

    // Filter clients based on search input
    this.filteredClients = this.clientFilterControl.valueChanges.pipe(
      startWith(''), // Initially show all clients
      switchMap(searchTerm => this.filterClients(searchTerm)) // Filter clients when search term changes
    );

    this.searchAuditLogsFormGroup.valueChanges.subscribe((formValue) => {
      const formControls = Object.keys(formValue);
      // console.log(this.searchAuditLogsFormGroup);
      const areAllFieldsEmpty = formControls.every((controlName) => {
        const control = this.searchAuditLogsFormGroup.get(controlName);
        return (
          control.value === null ||
          control.value === undefined ||
          control.value === ''
        );
      });

      if (areAllFieldsEmpty) {
        this.allFieldsEmpty = true;
        // console.log('All fields are empty');
      } else {
        this.allFieldsEmpty = false;
        // console.log('All fields are not empty');
      }
    });
    this.benefitService.isInternalUser().pipe(
      tap((internal) => {
        this.isInternalUser = internal;
        if (!internal) {
          this.displayedColumns = this.displayedColumns.filter(
            (type) => type !== 'errorCode' && type !== 'errorMessage' && type !== 'isSuccess'
          );
        }
      })
    ).subscribe();
  }

  // Function to filter clients based on the search term
  private filterClients(searchTerm: string): Observable<Clients[]> {
    const lowerCaseTerm = searchTerm.toLowerCase();
    return this.listOfClients$.pipe(
      map(clients =>
        clients.filter(client =>
          client.clientName.toLowerCase().includes(lowerCaseTerm) || client.clientId.includes(lowerCaseTerm)
        )
      )
    );
  }

  markFieldAsTouched() {
    this.searchAuditLogsFormGroup.get('cardId').markAsTouched();
  }

  isDemoClient(clientId: string) {
    this.loader.showLoader();
    this.demoClientService.checkDemoClient(clientId).subscribe({
      next: (res) => {
        // console.log('Response on isDemoClient:', res);
        this.demoClientService.updateDemoClientFlag = res;
      },
      error: (err) => {
        this.loader.hideLoader();
      },
      complete: () => {
        this.loader.hideLoader();
      },
    });
  }

  getAuditLogs() {
    return this.auditLogsService.searchAuditLogs();
  }

  endDateFilter = (date: Date | null): boolean => {
    const startDate = this.searchAuditLogsFormGroup.get('from').value as Date;

    // Disable dates before the selected start date
    return date >= startDate;
  };

  dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const fromDate = control.get('from').value;
    const toDate = control.get('to').value;

    if (fromDate && toDate && fromDate > toDate) {
      control.get('from').setErrors({ dateInvalid: true });
      control.get('to').setErrors({ dateInvalid: true });
      return { dateInvalid: true };
    }

    // Clear errors if validation passes
    // control.get('from').setErrors(null);
    // control.get('to').setErrors(null);
    return null;
  }

  checkDateValues() {
    // console
    //   .log
    // 'From Value, before:',
    // this.searchAuditLogsFormGroup.controls.from.value
    // 'after:',
    // new Date(this.searchAuditLogsFormGroup.controls.from.value).toISOString()
    // ();
    // console
    //   .log
    // 'To Value, before:',
    // this.searchAuditLogsFormGroup.controls.to.value
    // 'after:',
    // new Date(this.searchAuditLogsFormGroup.controls.to.value).toISOString()
    // ();
  }

  compareDates(a: string, b: string, isAsc: boolean): number {
    const dateA = new Date(a);
    const dateB = new Date(b);

    return (dateA < dateB ? -1 : 1) * (isAsc ? 1 : -1);
  }

  public onClientSelection(event: MatSelectChange) {
    this.sharedService.updateAccessLevel(event.value.olA_Access_Type);
    // console.log(event.value);
    if (this.dataLoaded) {
      this.resetForm();
      this.dataLoaded = false;
    }
    sessionStorage.setItem(AppConstants.LABELS.clientId, event.value.clientId);
    this.selectedClientId = event.value.clientId;
    this.isDemoClient(event.value.clientId);
  }

  formatDate(date: string): string {
    return formatDateToMMDDYYYY(date);
  }

  public searchAuditLogs() {
    this.loader.showLoader();

    this.selection = new SelectionModel<any>(true, []);
    const fromDate = this.searchAuditLogsFormGroup.controls.from.value;
    const toDate = this.searchAuditLogsFormGroup.controls.to.value;

    // Convert local date to UTC date
    let fromUTCDate = fromDate
      ? `${formatDate(new Date(fromDate))}T00:00:00.000Z`
      : null;

    let toUTCDate = toDate
      ? `${formatDate(new Date(toDate))}T23:59:59.999Z`
      : null;

    fromUTCDate = new Date(
      new Date(fromUTCDate).getTime() + new Date().getTimezoneOffset() * 60000
    ).toISOString();
    toUTCDate = new Date(
      new Date(toUTCDate).getTime() + new Date().getTimezoneOffset() * 60000
    ).toISOString();

    const reqBody: AuditLogsRequestInterface = {
      // emailId: this.authService.userEmail,
      clientId: this.selectedClientId,
      firstName: this.searchAuditLogsFormGroup.controls.firstName.value?.trim(),
      lastName: this.searchAuditLogsFormGroup.controls.lastName.value?.trim(),
      cardId: this.searchAuditLogsFormGroup.controls.cardId.value,
      emailId: this.searchAuditLogsFormGroup.controls.email.value?.trim(),
      category: this.searchAuditLogsFormGroup.controls.category.value,

      fromDate: fromUTCDate,
      toDate: toUTCDate,
      userType: this.isInternalUser ? 'internal' : 'external',
    };
    if (this.searchAuditLogsFormGroup.controls.status.value) {
      reqBody.status = this.searchAuditLogsFormGroup.controls.status.value === "true" ? true : false;
    }
    this.appInsightService.trackPageView(AppCenterAnalytics.GET_AUDIT_LOG_REQUEST, { data: reqBody });
    this.auditLogsService.inputSearchCriteria.next(reqBody);

    this.auditLogs$ = this.getAuditLogs().pipe(
      tap((res) => {
        // console.log('Response:', res);
      }),
      map((res) => {
        // console.log('Res:', res);
        if (res) {
          // this.dataSource = new MatTableDataSource(res);
          this.dataSource = new MatTableDataSource(res);
          // console.log('DataSource:', this.dataSource);
          setTimeout(() => {
            this.dataSource.paginator = this.paginator;
            // this.sort.sort({
            //   id: 'birthDt',
            //   start: 'desc',
            // } as MatSortable);
            this.dataSource.sort = this.sort;

            this.dataSource.sortingDataAccessor = (
              data: any,
              sortHeaderId: string
            ): string => {
              if (typeof data[sortHeaderId] === 'string') {
                return data[sortHeaderId].toLocaleLowerCase();
              }
            };
          }, 0);
          this.loader.hideLoader();
        }
        this.dataLoaded = true;
        return res;
      }),
      catchError((err) => {
        // console.log('Error:', err);
        this.dataLoaded = true;
        this.loader.hideLoader();
        return of([]);
      })
    );
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  parseComments(element: any): string {
    if (element.changes.includes('Requested Duplicate ID card')) {
      if (
        element.comments &&
        typeof element.comments === 'string' &&
        element.comments.trim() !== ''
      ) {
        return JSON.parse(element.comments).comment;
      } else {
        return '';
      }
    } else {
      return element.comments;
    }
  }

 
public printSelectedRows() {
  const selectedRows = this.selection.selected;
  if (!selectedRows || selectedRows.length === 0) return;

  // Locate the print iframe safely
  const printFrame = document.getElementById('printFrame') as HTMLIFrameElement | null;
  if (!printFrame) return;

  const printDoc = printFrame.contentDocument || printFrame.contentWindow?.document;
  if (!printDoc) return;

  // Shared cell styles (identical to original inline styles)
  const cellStyle = 'border: 1px solid #000; border-collapse: collapse; padding: 2px';
  const tableStyle = 'border: 1px solid #000; border-collapse: collapse; font-family: arial;';
  const errorMessageCellStyle = `${cellStyle}; width: 5px; overflow-wrap: break-word; word-wrap: break-word; white-space: normal;`;

  // Build thead (preserve columns and conditional internal-user fields)
  const thead = `
    <thead>
      <tr>
        <th style="${cellStyle}">Last Name</th>
        <th style="${cellStyle}">First Name</th>
        <th style="${cellStyle}">DOB</th>
        <th style="${cellStyle}">Person Code</th>
        <th style="${cellStyle}; width: 150px;">Group# and Name</th>
        <th style="${cellStyle}">Entry Date</th>
        <th style="${cellStyle}">Change</th>
        <th style="${cellStyle}">Comments</th>
        ${this.isInternalUser
          ? `
            <th style="${cellStyle}">Error Code</th>
            <th style="${cellStyle}">Error Message</th>
            <th style="${cellStyle}">Status</th>
          `
          : ''
        }
      </tr>
    </thead>
  `;

  // Build rows exactly as before (same field mappings, same date formats)
  const rowsHtml = selectedRows.map(row => {
    const birthDate = new Date(row.birthDt);
    const dateAdded = new Date(row.dateAdded);
    const actualBirthDateFormat = this.formatMMDDYYYY(birthDate);
    const actualDateAddedFormat = this.formatMMDDYYYY(dateAdded);

    const errCode = row.httpStatusCode ? row.httpStatusCode : '-';
    const errorMessage = row.errorMsg ? row.errorMsg : '-';
    const status = row.isSuccess === false ? 'Failed' : 'Successful';

    return `
      <tr>
        <td style="${cellStyle}">${row.lastName}</td>
        <td style="${cellStyle}">${row.firstName}</td>
        <td style="${cellStyle}">${actualBirthDateFormat}</td>
        <td style="${cellStyle}">${row.personCd}</td>
        <td style="${cellStyle}; width: 150px;">
          <div>${row.groupNo}</div>
          <div>${row.groupName}</div>
        </td>
        <td style="${cellStyle}">${actualDateAddedFormat}</td>
        <td style="${cellStyle}">${row.changes}</td>
        <td style="${cellStyle}">${row.comments}</td>
        ${this.isInternalUser
          ? `
            <td style="${cellStyle}">${errCode}</td>
            <td style="${errorMessageCellStyle}">${errorMessage}</td>
            <td style="${cellStyle}">${status}</td>
          `
          : ''
        }
      </tr>
    `;
  }).join('');

  // Compose final document
  const content = `
    <html>
      <head>
        <title>Print Selected Rows</title>
        <!-- <link rel="stylesheet" href="path_to_your_custom_styles.css"> -->
      </head>
      <body>
        <table style="${tableStyle}">
          ${thead}
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      </body>
    </html>
  `;

  // Write & print
  printDoc.open();
  printDoc.write(content);
  printDoc.close();

  printFrame.contentWindow?.print();
}

/* ========= Helper: preserves original MM/DD/YYYY formatting ========= */
private formatMMDDYYYY(date: Date): string {
  // Month: original logic (getMonth() + 1; zero-pad below 10)
  const month = date.getMonth() > 8 ? (date.getMonth() + 1).toString() : '0' + (date.getMonth() + 1);
  // Day: original logic (zero-pad below 10)
  const day = date.getDate() > 9 ? date.getDate().toString() : '0' + date.getDate();
  const year = date.getFullYear().toString();
  return `${month}/${day}/${year}`;
}


  createStyledTd(text: string, styles: Record<string, string>): string {
    let td = '<td style="';
    for (const style in styles) {
      td += style + ':' + styles[style] + ';';
    }
    td += '">' + text + '</td>';
    return td;
  }

  resetForm() {
    this.searchAuditLogsFormGroup.get('firstName').reset();
    this.searchAuditLogsFormGroup.get('lastName').reset();
    this.searchAuditLogsFormGroup.get('cardId').reset();
    this.searchAuditLogsFormGroup.get('from').reset();
    this.searchAuditLogsFormGroup.get('to').reset();
    this.searchAuditLogsFormGroup.get('status').reset();
    this.searchAuditLogsFormGroup.get('category').reset();
    this.searchAuditLogsFormGroup.get('email').reset();
  }
}
